'use client';

import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
} from 'motion/react';
import React, { useRef } from 'react';

import { ImageMedia } from '@/components/ImageMedia';
import { CMSLink } from '@/components/Link';
import RichText from '@/components/RichText';
import { Slider } from '@/components/Slider';
import { VideoMedia } from '@/components/VideoMedia';
import { useLocale } from '@/contexts/LocaleContext';
import { CareerList, CareersBlock, Media } from '@/payload-types';
import { cn } from '@/utils/cn';
import { getDictionary } from '@/utils/getDictionary';

import styles from './block.module.scss';

type CareersBlockProps = CareersBlock & {
  careers: CareerList;
};

const itemVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 50 },
};

export const CareersBlockClient: React.FC<CareersBlockProps> = (props) => {
  const {
    careers,
    content,
    image,
    mediaType,
    tracking,
    videoEmbedUrl,
    videoIsBackground,
    videoLoops,
    videoPoster,
  } = props;
  const locale = useLocale();
  const t = getDictionary(locale);

  const baseX = useMotionValue(0);
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const smoothVelocity = useSpring(scrollVelocity, {
    damping: 50,
    stiffness: 400,
  });
  const velocityFactor = useTransform(smoothVelocity, [0, 1000], [0, 5], {
    clamp: false,
  });

  const x = useTransform(baseX, (v) => `${v}deg`);

  const directionFactor = useRef<number>(1);
  useAnimationFrame((t, delta) => {
    let moveBy = directionFactor.current * -50 * (delta / 1000);

    /**
     * This is what changes the direction of the scroll once we
     * switch scrolling directions.
     */
    if (velocityFactor.get() < 0) {
      directionFactor.current = -1;
    } else if (velocityFactor.get() > 0) {
      directionFactor.current = 1;
    }

    moveBy += directionFactor.current * moveBy * velocityFactor.get();

    baseX.set(baseX.get() + moveBy);
  });

  const MediaComponent =
    mediaType === 'image' && image ? (
      <ImageMedia
        aspectRatio={'square'}
        image={image}
        responsiveWidths={{
          mobile: 343,
          desktop: 512,
          largeDesktop: 2256,
        }}
      />
    ) : mediaType === 'video' && videoEmbedUrl ? (
      <VideoMedia
        aspectRatio={'square'}
        videoPoster={videoPoster as Media}
        videoEmbedUrl={videoEmbedUrl}
        videoIsBackground={videoIsBackground as boolean}
        videoLoops={videoLoops as boolean}
      />
    ) : null;

  if (!careers || !Array.isArray(careers) || careers.length === 0) {
    return null;
  }

  return (
    <>
      <div className={cn('container', styles.intro)}>
        <div className={styles.content}>
          {content && <RichText content={content} enableGutter={false} />}
        </div>
        <motion.div
          className={styles.media}
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          style={{ willChange: 'opacity, transform' }}
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0 },
          }}
        >
          {MediaComponent}
        </motion.div>
      </div>
      <div className={styles.careers}>
        <div className={cn('container')}>
          <Slider
            divider
            HeaderContent={
              <motion.div className={styles.jobCircle}>
                <motion.svg
                  viewBox='0 0 160 160'
                  width='130'
                  height='130'
                  xmlns='http://www.w3.org/2000/svg'
                  xmlnsXlink='http://www.w3.org/1999/xlink'
                  className={styles.jobCircleSvg}
                  style={{ rotate: x, y: '-50%', x: '-50%' }}
                >
                  <defs>
                    <path
                      id='jobCircle'
                      d='M65,10 A55,55 0 1,1 64.99,10 Z'
                      transform='translate(15,15)'
                    />
                  </defs>
                  <text>
                    <textPath xlinkHref='#jobCircle'>
                      {t('careers.vacancies')} · {t('careers.vacancies')} ·
                    </textPath>
                  </text>
                </motion.svg>
                <span className={styles.jobCircleValue}>{careers.length}</span>
              </motion.div>
            }
          >
            {careers.map((item, index) => {
              const { description, link, title } = item;

              if (!title) {
                return null;
              }

              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  transition={{ duration: 0.6 }}
                  className={styles.item}
                >
                  <div className={styles.title}>{title}</div>
                  <div className={styles.description} title={description}>
                    {description}
                  </div>
                  {link && (
                    <div className={styles.link}>
                      <CMSLink
                        {...link}
                        appearance='level-4'
                        className='button--small'
                        tracking={{
                          type: 'Careers',
                          headline: tracking?.cta || '',
                          cta: title,
                        }}
                        ariaLabel={`${t('goTo')} ${title}`}
                      />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </Slider>
        </div>
      </div>
    </>
  );
};
